import React, { useState } from 'react';
import { X, Plus, Trash2, Upload, FileText } from 'lucide-react';
import { api } from '../lib/api';

const labelFor = (f) => f.label || (f.key.charAt(0).toUpperCase() + f.key.slice(1));
const inputCls = 'mt-1 w-full bg-white border border-neutral-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#D01919] transition text-sm';

function PdfField({ field, value, onChange, onCover }) {
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);
  const [err, setErr] = useState('');
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr(''); setBusy(true); setProg(0);
    try {
      const res = await api.uploadFile(f, setProg);
      onChange(res.url);
      if (res.coverUrl && onCover) onCover(res.coverUrl);
    } catch (e2) {
      setErr('Upload failed. Please try again.');
    } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-sm font-medium">{labelFor(field)}</label>
      <div className="mt-1 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-neutral-200 cursor-pointer px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Upload size={15} /> {busy ? `Uploading… ${prog}%` : 'Choose PDF'}
          <input type="file" accept="application/pdf" className="hidden" onChange={onFile} disabled={busy} />
        </label>
        {value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#D01919] hover:underline"><FileText size={15} /> View current PDF</a>
        ) : <span className="text-sm text-neutral-400">No PDF uploaded</span>}
      </div>
      {busy ? <div className="mt-2 h-1.5 bg-neutral-200 rounded overflow-hidden"><div className="h-full bg-[#D01919] transition-all" style={{ width: `${prog}%` }} /></div> : null}
      {err ? <p className="text-sm text-[#D01919] mt-1">{err}</p> : null}
    </div>
  );
}

function ImageField({ field, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(0);
  const [err, setErr] = useState('');
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setErr('Please choose an image file (PNG, JPG, WEBP).'); return; }
    setErr(''); setBusy(true); setProg(0);
    try {
      const res = await api.uploadFile(f, setProg);
      onChange(res.url);
    } catch (e2) {
      setErr('Upload failed. Please try again.');
    } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="text-sm font-medium">{labelFor(field)}</label>
      <div className="mt-1 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-neutral-200 cursor-pointer px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Upload size={15} /> {busy ? `Uploading… ${prog}%` : 'Upload image'}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} data-testid={`image-upload-${field.key}`} />
        </label>
        {value ? <img src={value} alt="preview" className="h-16 w-24 object-cover rounded-lg border border-neutral-200" /> : <span className="text-sm text-neutral-400">No image</span>}
      </div>
      {busy ? <div className="mt-2 h-1.5 bg-neutral-200 rounded overflow-hidden"><div className="h-full bg-[#D01919] transition-all" style={{ width: `${prog}%` }} /></div> : null}
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-2`} placeholder="…or paste an image URL" />
      {err ? <p className="text-sm text-[#D01919] mt-1">{err}</p> : null}
    </div>
  );
}

export function FieldInput({ field, value, onChange, onCover }) {
  const t = field.type || 'text';
  if (t === 'pdf') return <PdfField field={field} value={value} onChange={onChange} onCover={onCover} />;
  if (t === 'textarea') {
    return <div><label className="text-sm font-medium">{labelFor(field)}</label>
      <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} /></div>;
  }
  if (t === 'image') return <ImageField field={field} value={value} onChange={onChange} />;
  if (t === 'bool') {
    return <label className="flex items-center gap-2 mt-2 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#D01919]" />
      <span className="text-sm font-medium">{labelFor(field)}</span></label>;
  }
  if (t === 'stringList') {
    const text = Array.isArray(value) ? value.join('\n') : (value || '');
    return <div><label className="text-sm font-medium">{labelFor(field)} <span className="text-neutral-400">(one per line)</span></label>
      <textarea rows={5} value={text} onChange={(e) => onChange(e.target.value.split('\n').filter((x) => x.trim() !== ''))} className={`${inputCls} resize-y`} /></div>;
  }
  if (t === 'pairs') {
    const rows = Array.isArray(value) ? value : [];
    const update = (i, k, v) => { const next = rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r); onChange(next); };
    const add = () => onChange([...rows, { value: '', label: '' }]);
    const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
    return <div><label className="text-sm font-medium">{labelFor(field)}</label>
      <div className="space-y-2 mt-1">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={r.value || ''} onChange={(e) => update(i, 'value', e.target.value)} placeholder={field.ph?.[0] || 'Value'} className="flex-1 bg-white border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            <input value={r.label || ''} onChange={(e) => update(i, 'label', e.target.value)} placeholder={field.ph?.[1] || 'Label'} className="flex-[2] bg-white border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            <button type="button" onClick={() => remove(i)} className="text-neutral-400 hover:text-[#D01919]"><Trash2 size={16} /></button>
          </div>
        ))}
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm text-[#D01919] font-medium"><Plus size={15} /> Add row</button>
      </div></div>;
  }
  return <div><label className="text-sm font-medium">{labelFor(field)}</label>
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} /></div>;
}

export function Modal({ open, onClose, title, children, onSave, saving }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center p-4 overflow-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-black"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-auto">{children}</div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-neutral-100">Cancel</button>
          <button onClick={onSave} disabled={saving} className="px-5 py-2 rounded-lg text-sm font-medium bg-[#111111] text-white hover:bg-[#D01919] transition-colors disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
